import React, { useEffect, useMemo, useRef, useState } from "react";
import ConfirmModal from "../../common/ConfirmModal";
import { fetchDocumentComments } from "./qmsService";
import {
  VAULT_COMMENT_PRIORITIES,
  buildVaultCommentThreads,
  canModifyVaultComment,
  getVaultCommentPriorityClass,
  normalizeVaultCommentPriority,
  parseVaultTaggedStaffIds,
} from "./qmsVaultWorkspaceUtils";
import { normalizeSuccessStoryList } from "./qmsApiUtils";

function getStaffLabel(staff) {
  return (
    staff?.UserName ||
    [staff?.FirstName, staff?.LastName].filter(Boolean).join(" ") ||
    staff?.StaffName ||
    "User"
  );
}

function getStaffId(staff) {
  return String(staff?.StaffId || staff?.staffId || "");
}

function getInitials(name) {
  const parts = String(name || "U")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
  }
  return (parts[0]?.[0] || "U").toUpperCase();
}

function renderCommentBody(text) {
  const value = String(text || "");
  if (!value) return null;

  const segments = value.split(/(@[^\n@]+?)(?=\s|$|[.,!?;:])/g);
  return segments.map((segment, index) => {
    if (segment.startsWith("@")) {
      return (
        <span key={`mention-${index}`} className="qms-vault-comments__mention-inline">
          {segment}
        </span>
      );
    }
    return <React.Fragment key={`text-${index}`}>{segment}</React.Fragment>;
  });
}

function PriorityPills({ value, disabled, onChange }) {
  const selected = normalizeVaultCommentPriority(value);
  return (
    <div
      className="qms-vault-comments__priority-seg"
      role="radiogroup"
      aria-label="Comment priority"
    >
      {VAULT_COMMENT_PRIORITIES.map((option) => {
        const active = selected === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            className={`qms-vault-comments__priority-option ${getVaultCommentPriorityClass(
              option.value,
            )}${active ? " is-active" : ""}`}
            disabled={disabled}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (disabled || active) return;
              onChange(option.value);
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function MentionTextarea({
  value,
  mentionIds,
  staffOptions,
  disabled,
  placeholder,
  onChange,
  onMentionIdsChange,
}) {
  const textareaRef = useRef(null);
  const menuRef = useRef(null);
  const [mentionMenu, setMentionMenu] = useState(null);

  const mentionCandidates = useMemo(() => {
    if (!mentionMenu) return [];
    const query = mentionMenu.query.trim().toLowerCase();
    const available = staffOptions.filter((staff) => {
      const id = getStaffId(staff);
      return id && !mentionIds.includes(id);
    });
    if (!query) return available;
    return available.filter((staff) =>
      getStaffLabel(staff).toLowerCase().includes(query),
    );
  }, [mentionMenu, mentionIds, staffOptions]);

  const syncMentionMenu = (nextValue, cursorPosition) => {
    const textBeforeCursor = nextValue.slice(0, cursorPosition);
    const match = textBeforeCursor.match(/@([^\s@]*)$/);
    if (!match) {
      setMentionMenu(null);
      return;
    }
    setMentionMenu({
      start: cursorPosition - match[0].length,
      query: match[1] || "",
      activeIndex: 0,
    });
  };

  const handleChange = (event) => {
    const nextValue = event.target.value;
    onChange(nextValue);
    syncMentionMenu(nextValue, event.target.selectionStart);
  };

  const handleKeyDown = (event) => {
    if (!mentionMenu || mentionCandidates.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setMentionMenu((current) => ({
        ...current,
        activeIndex: Math.min(current.activeIndex + 1, mentionCandidates.length - 1),
      }));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setMentionMenu((current) => ({
        ...current,
        activeIndex: Math.max(current.activeIndex - 1, 0),
      }));
      return;
    }

    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      insertMention(mentionCandidates[mentionMenu.activeIndex]);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setMentionMenu(null);
    }
  };

  const insertMention = (staff) => {
    if (!staff || !mentionMenu || !textareaRef.current) return;

    const label = getStaffLabel(staff);
    const id = getStaffId(staff);
    const cursor = textareaRef.current.selectionStart;
    const before = value.slice(0, mentionMenu.start);
    const after = value.slice(cursor);
    const mentionText = `@${label} `;
    const nextValue = `${before}${mentionText}${after}`;
    const nextCursor = before.length + mentionText.length;

    onChange(nextValue);
    onMentionIdsChange((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setMentionMenu(null);

    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(nextCursor, nextCursor);
    });
  };

  useEffect(() => {
    if (!mentionMenu) return undefined;

    const handlePointerDown = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      if (textareaRef.current?.contains(event.target)) return;
      setMentionMenu(null);
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [mentionMenu]);

  return (
    <div className="qms-vault-comments__input-shell">
      <textarea
        ref={textareaRef}
        className="qms-vault-comments__input"
        rows={3}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={(event) => syncMentionMenu(value, event.target.selectionStart)}
      />

      {mentionMenu ? (
        <div ref={menuRef} className="qms-vault-comments__mention-menu" role="listbox">
          {staffOptions.length === 0 ? (
            <p className="qms-vault-comments__mention-empty">
              Assign reviewers or approvers before mentioning them.
            </p>
          ) : mentionCandidates.length === 0 ? (
            <p className="qms-vault-comments__mention-empty">No matches for @{mentionMenu.query}</p>
          ) : (
            mentionCandidates.map((staff, index) => {
              const id = getStaffId(staff);
              const label = getStaffLabel(staff);
              return (
                <button
                  key={id}
                  type="button"
                  role="option"
                  aria-selected={index === mentionMenu.activeIndex}
                  className={`qms-vault-comments__mention-option${
                    index === mentionMenu.activeIndex ? " is-active" : ""
                  }`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => insertMention(staff)}
                >
                  <span className="qms-vault-comments__mention-avatar">{getInitials(label)}</span>
                  <span className="qms-vault-comments__mention-label">{label}</span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}

function CommentComposer({
  draft,
  priority,
  mentionIds,
  staffOptions,
  saving,
  submitLabel,
  onDraftChange,
  onPriorityChange,
  onMentionIdsChange,
  onCancel,
  onSubmit,
  showCancel = false,
}) {
  return (
    <div className="qms-vault-comments__composer-card">
      <MentionTextarea
        value={draft}
        mentionIds={mentionIds}
        staffOptions={staffOptions}
        disabled={saving}
        placeholder="Write a comment… Type @ to mention someone"
        onChange={onDraftChange}
        onMentionIdsChange={onMentionIdsChange}
      />

      <div className="qms-vault-comments__composer-toolbar">
        <PriorityPills
          value={priority}
          disabled={saving}
          onChange={onPriorityChange}
        />

        <div className="qms-vault-comments__toolbar-actions">
          {showCancel ? (
            <button
              type="button"
              className="qms-vault-comments__btn-ghost"
              disabled={saving}
              onClick={onCancel}
            >
              Cancel
            </button>
          ) : null}
          <button
            type="button"
            className="qms-vault-comments__btn-primary"
            disabled={saving || !draft.trim()}
            onClick={onSubmit}
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                Saving…
              </>
            ) : (
              submitLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function CommentItem({
  comment,
  isReply = false,
  currentStaffId,
  saving,
  pendingAction,
  onReply,
  onEdit,
  onDelete,
}) {
  const canModify = canModifyVaultComment(comment, currentStaffId);
  const priorityLabel = normalizeVaultCommentPriority(comment.CommentPriority);
  const priorityClass = getVaultCommentPriorityClass(priorityLabel);
  const staffName = comment.StaffName || "User";
  const isDeleting =
    pendingAction?.type === "delete" &&
    String(pendingAction.commentId) === String(comment.CommentId);
  const showActions = !isReply || canModify;

  return (
    <article
      className={`qms-vault-comments__item${isReply ? " is-reply" : ""}${
        isDeleting ? " is-removing" : ""
      }`}
    >
      <header className="qms-vault-comments__item-head">
        <div className="qms-vault-comments__item-meta">
          <strong className="qms-vault-comments__item-author">{staffName}</strong>
          {comment.SystemDateTime ? (
            <span className="qms-vault-comments__item-time">
              {comment.SystemDateTime}
            </span>
          ) : null}
          {comment.UpdatedDateTime ? (
            <span className="qms-vault-comments__edited">
              edited {comment.UpdatedDateTime}
            </span>
          ) : null}
        </div>
        <span className={`qms-vault-comments__priority-badge ${priorityClass}`}>
          {priorityLabel}
        </span>
      </header>

      <div className="qms-vault-comments__item-body">
        {renderCommentBody(comment.Comment)}
      </div>

      {showActions ? (
        <footer className="qms-vault-comments__item-actions">
          {!isReply ? (
            <button
              type="button"
              className="qms-vault-comments__action-link"
              disabled={saving}
              onClick={() => onReply(comment)}
            >
              Reply
            </button>
          ) : null}
          {canModify ? (
            <>
              <button
                type="button"
                className="qms-vault-comments__action-link"
                disabled={saving}
                onClick={() => onEdit(comment)}
              >
                Edit
              </button>
              <button
                type="button"
                className="qms-vault-comments__action-link is-danger"
                disabled={saving}
                onClick={() => onDelete(comment)}
              >
                {isDeleting ? (
                  <>
                    <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                    Removing…
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </>
          ) : null}
        </footer>
      ) : null}
    </article>
  );
}

function QmsVaultCommentsPanel({
  active = true,
  variant = "default",
  documentId,
  userData,
  staffOptions = [],
  saving = false,
  onSave,
  onEdit,
  onDelete,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [mentionIds, setMentionIds] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const currentStaffId = String(userData?.StaffId || "");

  const loadComments = async () => {
    if (!documentId || !userData?.StaffId) return;
    setLoading(true);
    setError("");
    try {
      const response = await fetchDocumentComments(userData, documentId);
      setComments(normalizeSuccessStoryList(response));
    } catch (err) {
      setError(err?.message || "Could not load comments.");
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!active) return;
    loadComments();
  }, [active, documentId, userData?.StaffId]);

  useEffect(() => {
    if (!saving) setPendingAction(null);
  }, [saving]);

  const threads = useMemo(() => buildVaultCommentThreads(comments), [comments]);

  const resetComposer = () => {
    setDraft("");
    setPriority("Medium");
    setMentionIds([]);
    setReplyTo(null);
    setEditingComment(null);
  };

  const buildPayload = () => ({
    comment: draft.trim(),
    commentPriority: priority,
    assignedStaff: mentionIds.join(","),
    previousCommentId: replyTo?.CommentId || "",
  });

  const handleCreate = async () => {
    if (!draft.trim() || saving) return;
    setPendingAction({ type: "create" });
    try {
      await onSave?.(buildPayload());
      resetComposer();
      await loadComments();
    } catch {
      // Parent shows error toast.
    }
  };

  const handleEdit = async () => {
    if (!editingComment || !draft.trim() || saving) return;
    setPendingAction({ type: "edit", commentId: editingComment.CommentId });
    try {
      await onEdit?.({
        commentId: editingComment.CommentId,
        ...buildPayload(),
        previousCommentId: editingComment.PreviousCommentId || "",
      });
      resetComposer();
      await loadComments();
    } catch {
      // Parent shows error toast.
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete || saving) return;
    setPendingAction({ type: "delete", commentId: confirmDelete.CommentId });
    setConfirmDelete(null);
    try {
      await onDelete?.({ commentId: confirmDelete.CommentId });
      if (editingComment?.CommentId === confirmDelete.CommentId) resetComposer();
      if (replyTo?.CommentId === confirmDelete.CommentId) setReplyTo(null);
      await loadComments();
    } catch {
      // Parent shows error toast.
    }
  };

  const startReply = (comment) => {
    setEditingComment(null);
    setReplyTo(comment);
    setDraft("");
    setPriority("Medium");
    setMentionIds([]);
  };

  const startEdit = (comment) => {
    setReplyTo(null);
    setEditingComment(comment);
    setDraft(comment.Comment || "");
    setPriority(normalizeVaultCommentPriority(comment.CommentPriority));
    setMentionIds(parseVaultTaggedStaffIds(comment.TaggedStaffIds));
  };

  const composerSaving =
    saving &&
    (pendingAction?.type === "create" ||
      pendingAction?.type === "edit" ||
      pendingAction?.type === "delete");

  const isRail = variant === "rail";

  return (
    <div
      className={`qms-doc-workspace__panel qms-vault-workspace__comments-panel qms-vault-comments${
        isRail ? " is-rail" : ""
      }`}
    >
      <div className="qms-doc-workspace__panel-body qms-vault-comments__feed-area">
        {loading ? (
          <div className="qms-vault-comments__empty-state">
            <i className="fas fa-spinner fa-spin" aria-hidden="true" />
            <span>Loading comments…</span>
          </div>
        ) : error ? (
          <p className="text-danger small mb-0">{error}</p>
        ) : threads.length === 0 ? (
          <div className="qms-vault-comments__empty-state">
            <i className="far fa-comment-dots" aria-hidden="true" />
            <span>No comments yet. Start the conversation.</span>
          </div>
        ) : (
          <div className="qms-vault-comments__feed">
            {threads.map((thread) => (
              <div key={thread.CommentId} className="qms-vault-comments__thread">
                <CommentItem
                  comment={thread}
                  currentStaffId={currentStaffId}
                  saving={saving}
                  pendingAction={pendingAction}
                  onReply={startReply}
                  onEdit={startEdit}
                  onDelete={setConfirmDelete}
                />
                {thread.replies?.length ? (
                  <div className="qms-vault-comments__replies">
                    {thread.replies.map((reply) => (
                      <CommentItem
                        key={reply.CommentId}
                        comment={reply}
                        isReply
                        currentStaffId={currentStaffId}
                        saving={saving}
                        pendingAction={pendingAction}
                        onReply={startReply}
                        onEdit={startEdit}
                        onDelete={setConfirmDelete}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="qms-vault-comments__composer-area">
        {replyTo ? (
          <div className="qms-vault-comments__context-banner is-reply">
            <span>
              Replying to <strong>{replyTo.StaffName || "comment"}</strong>
            </span>
            <button
              type="button"
              className="qms-vault-comments__banner-dismiss"
              onClick={() => setReplyTo(null)}
            >
              Cancel
            </button>
          </div>
        ) : null}

        {editingComment ? (
          <div className="qms-vault-comments__context-banner is-edit">
            <span>
              Editing comment from <strong>{editingComment.SystemDateTime}</strong>
            </span>
            <button
              type="button"
              className="qms-vault-comments__banner-dismiss"
              onClick={resetComposer}
            >
              Cancel
            </button>
          </div>
        ) : null}

        <div className="qms-vault-comments__mention-hint" role="note">
          Type <strong>@</strong> to mention reviewers or approvers.
        </div>

        <form
          className="qms-vault-workspace__comment-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (editingComment) handleEdit();
            else handleCreate();
          }}
        >
          <CommentComposer
            draft={draft}
            priority={priority}
            mentionIds={mentionIds}
            staffOptions={staffOptions}
            saving={composerSaving}
            submitLabel={
              editingComment
                ? "Save changes"
                : replyTo
                  ? "Post reply"
                  : "Post comment"
            }
            showCancel={Boolean(editingComment)}
            onDraftChange={setDraft}
            onPriorityChange={setPriority}
            onMentionIdsChange={setMentionIds}
            onCancel={resetComposer}
            onSubmit={editingComment ? handleEdit : handleCreate}
          />
        </form>
      </div>

      <ConfirmModal
        isOpen={Boolean(confirmDelete)}
        title="Delete comment"
        message="Delete this comment? This cannot be undone."
        confirmText={pendingAction?.type === "delete" ? "Deleting…" : "Delete"}
        confirmButtonClass="btn-danger"
        icon="fas fa-trash text-danger"
        onClose={() => {
          if (saving) return;
          setConfirmDelete(null);
        }}
        onConfirm={handleDelete}
      />
    </div>
  );
}

export default QmsVaultCommentsPanel;
